import { iconPath } from '@ui/helpers'
import { useReviews } from '@ui/hooks'
import { FC, useMemo, useState } from 'react'
import css from 'styled-jsx/css'
import Pagination from '../shared/Pagination'
import StarRating from '../shared/StarRating'
import ReviewModal from './ReviewModal'
import { ReviewImage } from './ReviewImage'
//import ReviewModal from '../shared/Modal/ReviewModal'

type ReviewsProps = {
  perPage?: number
  className?: string
}

const Reviews: FC<ReviewsProps> = ({ perPage = 3, className }: ReviewsProps) => {
  const [sellpageReviews] = useReviews()
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [cPerPage, setCPerPage] = useState(perPage)
  const [reviewNums] = useState(sellpageReviews.length)

  const showMore = () => {
    setIsLoading(true)
    setTimeout(() => {
      setCPerPage(cPerPage + 3)
      setIsLoading(false)
    }, 300)
  }

  const toggleModal = () => setIsOpen(!isOpen)

  const onClickPage = (pageNum: number) => setActivePage(pageNum)

  const currentReviews = useMemo(() => {
    const start = (activePage - 1) * cPerPage
    return sellpageReviews.slice(start, start + cPerPage)
  }, [activePage, cPerPage, sellpageReviews])

  const totalReviews: number = useMemo(() => {
    return sellpageReviews?.length || 0
  }, [sellpageReviews])

  const avgRated = useMemo(() => {
    if (totalReviews) {
      const totalRated = sellpageReviews?.reduce(
        (total: any, review: any) => total + review.rating,
        0
      )
      return Math.ceil(totalRated / totalReviews)
    } else {
      return 0
    }
  }, [sellpageReviews])

  return (
    <>
      {!!sellpageReviews.length && (
        <div className={`sellpageReviews ${className}`}>
          <style jsx>{scopedStyle}</style>
          <style jsx>{imgZoomStyle}</style>
          <div className="columns">
            <div className="column is-4">
              <div className="title--large">Reviews</div>
            </div>
            <div className="column is-8">
              <div className="level">
                <div className="level-left is-flex-mobile">
                  <div className="level-item">
                    <div className="sellpageReviews--title">{totalReviews} Reviews</div>
                    <StarRating rating={avgRated} className="has-text-left" />
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <a
                      className="sellpageReviews--button button is-primary is-fullwidth-mobile"
                      onClick={toggleModal}
                    >
                      <figure className="image is-16x16">
                        <img src={iconPath('pen.svg')} alt="" />
                      </figure>
                      write your review
                    </a>
                  </div>
                </div>
              </div>
              <hr className="hr" />

              {currentReviews?.length > 0 &&
                currentReviews.map((review, idx: number) => (
                  <div className="columns mb-0" key={idx}>
                    <div className="column is-3">
                      <div>{review?.name}</div>
                      <p>
                        <small>Verified Buyer</small>
                      </p>
                      <StarRating key="idx" rating={review?.rating} />
                    </div>
                    <div className="review column is-9">
                      <div className="review--title">{review?.title ?? ''}</div>
                      {review?.images?.length >= 1 && <ReviewImage source={review?.images[0]} />}
                      <div className="review--content">{review?.description ?? ''}</div>
                      {idx < currentReviews.length - 1 && <hr className="m-0" />}
                    </div>
                  </div>
                ))}

              {reviewNums > perPage && (
                <Pagination
                  total={reviewNums}
                  perPage={perPage}
                  active={activePage}
                  className="is-hidden-mobile"
                  onPageChanged={onClickPage}
                />
              )}

              <div className="show-more-button">
                <button
                  className={`button button--cta is-primary is-outlined is-hidden-tablet ${
                    cPerPage >= reviewNums ? 'is-hidden' : ''
                  } ${isLoading ? 'is-loading' : ''}`}
                  onClick={showMore}
                >
                  Show more
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ReviewModal open={isOpen} toggleModal={toggleModal} />
    </>
  )
}

const scopedStyle = css`
  .sellpageReviews {
    &--button {
      padding: 15px;
      height: auto;

      .image {
        margin-right: 10px;
      }
    }

    &--title {
      font-size: 20px;
      font-weight: 700;
      line-height: 30px;
      margin-right: 17px;
    }
  }

  .review {
    margin-bottom: 13px;

    &--content {
      margin-bottom: 45px;
    }

    &--title {
      font-weight: 700;
      margin-bottom: 8px;
    }
  }

  .hr {
    margin-bottom: 60px;
  }

  .show-more-button {
    text-align: center;

    button {
      padding-right: 25px;
      padding-left: 25px;

      &:hover,
      &:focus {
        background-color: transparent !important;
        color: var(--text) !important;
      }

      &.is-loading {
        background-color: var(--text) !important;
      }
    }
  }
`

const imgZoomStyle = css`
  .zoomin img {
    height: 90px;
    width: 90px;
    -webkit-transition: all 1s ease;
    -moz-transition: all 1s ease;
    -ms-transition: all 1s ease;
    transition: all 1s ease;
    cursor: pointer;
  }
  .zoomin img:hover {
    width: 350px;
    height: 350px;
  }
`

export default Reviews
